import { useState } from "react";
import { useClientTranslations } from "../../../../lib/i18n/client";
import type { EventVisualCategory } from "../../../../lib/track/eventTypeColor";
import {
	type CustomFrequencyState,
	type FrequencyPreset,
	serializeRRule,
	type Weekday,
} from "../../../../lib/track/serializeRRule";
import { DatePicker } from "./DatePicker";
import { FormField } from "./FormField";
import styles from "./FrequencyPicker.module.css";
import { Select, type SelectOption } from "./Select";

export interface FrequencyValue {
	preset: FrequencyPreset;
	custom?: CustomFrequencyState;
	/** Pre-serialized RRULE — null when preset='once'. */
	rrule: string | null;
	/**
	 * Reserved for future use. Phase 37 WR-05: parent forms no longer rewrite
	 * the dateTime hour from this value — BID/TID preserve the user's chosen
	 * time and let the server expansion start from that hour. Always `null`
	 * today; left in the interface so callers don't need to be updated when
	 * (and if) we re-introduce a hour-locking UX.
	 */
	firstHour: number | null;
}

interface FrequencyPickerProps {
	id: string;
	label: string;
	required?: boolean;
	error?: string;
	value: FrequencyValue;
	onChange: (next: FrequencyValue) => void;
	accentCategory?: EventVisualCategory;
	placeholder?: string;
}

const PRESETS: readonly FrequencyPreset[] = [
	"once",
	"daily",
	"bid",
	"tid",
	"eod",
	"custom",
] as const;

const WEEKDAYS: readonly Weekday[] = [
	"MO",
	"TU",
	"WE",
	"TH",
	"FR",
	"SA",
	"SU",
] as const;

const WEEKDAY_KEYS: Record<
	Weekday,
	"mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun"
> = {
	MO: "mon",
	TU: "tue",
	WE: "wed",
	TH: "thu",
	FR: "fri",
	SA: "sat",
	SU: "sun",
};

function defaultCustom(): CustomFrequencyState {
	return { weekdays: [], endMode: "count", count: 1 };
}

function isFrequencyPreset(v: string): v is FrequencyPreset {
	return (PRESETS as readonly string[]).includes(v);
}

/**
 * Phase 37 — Frequency picker. Preset selection via a Select dropdown
 * (Once / Daily / Twice daily / Three times daily / Every other day / Custom…).
 * Custom expands inline below the trigger with weekday chips + ends mode.
 *
 * D-13: emits a serialized RRULE on every change; era-api validates via
 * rrule.js on receipt. WR-05: BID/TID no longer rewrite the dateTime hour —
 * the user's chosen time is preserved and the server expansion starts from
 * that hour.
 */
export function FrequencyPicker({
	id,
	label,
	required,
	error,
	value,
	onChange,
	accentCategory = "meds",
	placeholder,
}: FrequencyPickerProps) {
	const { t } = useClientTranslations("track");
	// Hydrate the UNTIL DatePicker's local state from value.custom.until so an
	// edit-mode form opening on a recurring series with FREQ=WEEKLY;UNTIL=...
	// doesn't reopen with a blank UNTIL picker (PR-102 review #1).
	const [customDate, setCustomDate] = useState<Date | undefined>(() => {
		const until = value.custom?.until;
		if (!until) return undefined;
		const [y, m, d] = until.split("-").map(Number);
		if (!y || !m || !d) return undefined;
		return new Date(y, m - 1, d);
	});

	const presetOptions: SelectOption[] = PRESETS.map((preset) => ({
		value: preset,
		label: t(`event.frequency.presets.${preset}`),
	}));

	const handlePreset = (preset: FrequencyPreset) => {
		const custom =
			preset === "custom" ? (value.custom ?? defaultCustom()) : undefined;
		onChange({
			preset,
			custom,
			rrule: serializeRRule(preset, custom),
			firstHour: null,
		});
	};

	const updateCustom = (patch: Partial<CustomFrequencyState>) => {
		const merged: CustomFrequencyState = {
			...(value.custom ?? defaultCustom()),
			...patch,
		};
		onChange({
			preset: "custom",
			custom: merged,
			rrule: serializeRRule("custom", merged),
			firstHour: null,
		});
	};

	const toggleWeekday = (d: Weekday) => {
		const cur = value.custom?.weekdays ?? [];
		const next = cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d];
		updateCustom({ weekdays: next });
	};

	return (
		<FormField id={id} label={label} required={required} error={error}>
			<Select
				id={id}
				value={value.preset}
				options={presetOptions}
				placeholder={placeholder ?? t("event.add.fields.frequency.placeholder")}
				onChange={(v) => {
					if (isFrequencyPreset(v)) handlePreset(v);
				}}
			/>
			{value.preset === "custom" ? (
				<div className={styles.customPanel}>
					<fieldset className={styles.weekdayRow}>
						<legend className={styles.sectionLabel}>
							{t("event.frequency.custom.weekdaysLabel")}
						</legend>
						{WEEKDAYS.map((d) => {
							const sel = (value.custom?.weekdays ?? []).includes(d);
							return (
								<button
									key={d}
									type="button"
									aria-pressed={sel}
									className={`${styles.weekdayChip} ${
										sel
											? `${styles.weekdayChipSelected} ${
													styles[`weekdayChipSelected--${accentCategory}`]
												}`
											: ""
									}`}
									onClick={() => toggleWeekday(d)}
								>
									{t(`event.frequency.custom.weekdays.${WEEKDAY_KEYS[d]}`)}
								</button>
							);
						})}
					</fieldset>
					<fieldset className={styles.endsRow}>
						<legend className={styles.sectionLabel}>
							{t("event.frequency.custom.endsLabel")}
						</legend>
						<label className={styles.endsRadio}>
							<input
								type="radio"
								name={`${id}-endMode`}
								checked={value.custom?.endMode === "until"}
								onChange={() => updateCustom({ endMode: "until" })}
							/>
							{t("event.frequency.custom.endsModes.until")}
						</label>
						<label className={styles.endsRadio}>
							<input
								type="radio"
								name={`${id}-endMode`}
								checked={value.custom?.endMode === "count"}
								onChange={() => updateCustom({ endMode: "count" })}
							/>
							{t("event.frequency.custom.endsModes.count")}
						</label>
					</fieldset>
					{value.custom?.endMode === "until" ? (
						<DatePicker
							id={`${id}-until`}
							label={t("event.frequency.custom.untilLabel")}
							selected={customDate}
							onSelect={(d) => {
								setCustomDate(d ?? undefined);
								if (d) {
									const yyyy = d.getFullYear();
									const mm = String(d.getMonth() + 1).padStart(2, "0");
									const dd = String(d.getDate()).padStart(2, "0");
									updateCustom({ until: `${yyyy}-${mm}-${dd}` });
								}
							}}
							fromDate={new Date()}
							disabled={{ before: new Date() }}
						/>
					) : (
						<label className={styles.countRow}>
							<span className={styles.countLabel}>
								{t("event.frequency.custom.countLabel")}
							</span>
							<input
								type="number"
								min={1}
								max={365}
								className={styles.countInput}
								value={value.custom?.count ?? 1}
								onChange={(e) =>
									updateCustom({ count: Number(e.target.value) || 1 })
								}
							/>
						</label>
					)}
				</div>
			) : null}
		</FormField>
	);
}
